<script lang="ts">
	import { format } from '../../../services/i18n';
	import {
		ActionsContainer,
		IconButton,
		LabelList,
		LabelListV2,
		ListMenuCard,
		ListMenuCardV2,
	} from '@Shelly/shelly-design-system/index';

	import { edit } from 'src/stores/ui/edit_lists';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import ControlsScene from './ControlsScene.svelte';
	import type { ISceneObj } from '../../../types/SceneType';
	import { mapIconSolidWeightToLight, mapLegacyColorToToken } from 'src/helpers/new-layout-helpers';
	import { updateItemStoreAfterPulse } from 'src/helpers/add-entity-helpers';

	export let item: ISceneObj;
	export let load_image_from: (path: string) => string | undefined;
	export let onCardClick: (item: ISceneObj) => void;
	export let onCardDelete: (e: MouseEvent | TouchEvent, item: ISceneObj) => void;
	export let onCardEdit: (e: MouseEvent | TouchEvent, item: ISceneObj) => void;
	export let sceneLabels: Record<string, Array<any>>;
	export let isRoomFilter: boolean;

	$: cardBackgroundColor = item?._meta?.backgroundColor as any;

	$: if (item?._meta?.new && $edit) {
		// Remove pulse when added new item and we are in edit mode
		updateItemStoreAfterPulse(item, 'scenes');
	}
</script>

{#if $showNewLayout}
	<ListMenuCardV2
		id={`scene-card-${item._id}`}
		title={item._meta.name}
		image={item._meta.image && load_image_from(item._meta.image)}
		backgroundColor={mapLegacyColorToToken(cardBackgroundColor)}
		editMode={$edit}
		selected={item._id === $sidePanelStateCombined?.id}
		hideSlotMain={sceneLabels[item._id]?.length < 1}
		isFocusable={false}
		on:click={() => onCardClick(item)}
		on:handle-drag-start
		on:handle-drag-end
		pulseAnimation={item?._meta?.new}
		on:pulse-animation-end={() => {
			updateItemStoreAfterPulse(item, 'scenes');
		}}
	>
		<LabelListV2
			slot="main"
			size="m"
			labelList={sceneLabels[item._id]?.map((el) => ({
				...el,
				icon: mapIconSolidWeightToLight(el?.icon),
				backgroundColor: mapLegacyColorToToken(el?.type),
			})) || []}
			a11yProps={{ isInteractive: true }}
		/>
		<svelte:fragment slot="right">
			<ControlsScene {item} variant="listMenuCard" />
		</svelte:fragment>
	</ListMenuCardV2>
{:else}
	<ListMenuCard
		id={`scene-card-${item._id}`}
		title={item._meta.name}
		image={item._meta.image && load_image_from(item._meta.image)}
		backgroundColor={cardBackgroundColor}
		editMode={$edit}
		selected={item._id === $sidePanelStateCombined?.id}
		hideSlotMain={sceneLabels[item._id]?.length < 1}
		on:click={() => onCardClick(item)}
		isFocusable={false}
		pulseAnimation={item?._meta?.new}
		on:pulse-animation-end={() => {
			updateItemStoreAfterPulse(item, 'scenes');
		}}
	>
		<LabelList size="m" labelList={sceneLabels[item._id] || []} slot="main" />
		<svelte:fragment slot="right">
			{#if $edit}
				<ActionsContainer orientation="vertical">
					<IconButton
						on:mousedown
						on:touchstart
						on:mouseup
						on:touchend
						icon="fas fa-up-down-left-right"
						slot="first"
						dndHandle
						disabled={!isRoomFilter}
						class={classMap({ 'cursor-not-allowed': !isRoomFilter })}
						aria-label={$format('aria_drag_scene', {
							default: 'Drag scene',
						})}
					/>
					<IconButton
						icon="fas fa-pen"
						slot="second"
						on:click={(e) => onCardEdit(e, item)}
						aria-label={$format('aria_edit_scene', {
							default: 'Edit scene',
						})}
					/>
					<IconButton
						icon="fas fa-trash"
						variant="danger"
						slot="third"
						on:click={(e) => onCardDelete(e, item)}
						aria-label={$format('aria_delete_scene', {
							default: 'Delete scene',
						})}
					/>
				</ActionsContainer>
			{:else}
				<ControlsScene {item} variant="listMenuCard" />
			{/if}
		</svelte:fragment>
	</ListMenuCard>
{/if}
